import { useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';
import { passesService } from '@/features/payment';
import type { Pass } from '@/types/passes';

const PASSES_KEY = 'passes';

/**
 * Pro / Designer Pass catalog. Copy + price + `is_active` are server-driven
 * (MONETIZATION_PIVOT principle 5) and locale-sensitive, so the locale is part
 * of the cache key — switching language re-fetches localized copy. Used by both
 * the Tokens-screen Pass cards and the Pass paywall dialog.
 *
 * `enabled` gates the fetch: `/v1/passes` is auth-only, but the paywall dialog
 * is root-mounted (app/providers.tsx) so it instantiates this hook on every
 * page — including pre-auth screens like login. Without the gate the query
 * fires unauthenticated and the endpoint 401s. Callers pass the dialog-open
 * condition so the catalog loads only when it's actually about to be shown.
 */
export function usePasses({ enabled = true }: { enabled?: boolean } = {}) {
  const locale = useLocale();
  return useQuery<Pass[]>({
    queryKey: [PASSES_KEY, locale],
    queryFn: async () => {
      const response = await passesService.getPasses();
      return response.data;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 min — pass copy/price is stable
    gcTime: 30 * 60 * 1000,
  });
}
